import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";
import {
	ChevronDown,
	ChevronRight,
	GripVertical,
	MoreHorizontal,
	Pencil,
	Plus,
	Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { STAGE_KIND } from "@/features/stages/utils/kind.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useDeleteTemplateItem } from "../../hooks/use-delete-template-item.ts";
import { useReorderTemplateItem } from "../../hooks/use-reorder-template-item.ts";
import type { TemplateItem } from "../../types.ts";
import {
	childrenCount,
	descendantIds,
	moveSiblingBlock,
	visibleItems,
} from "../../utils/tree.ts";
import { TemplateItemDialog } from "../template-item-dialog";

function deleteDescription(item: TemplateItem, descendants: number): string {
	if (descendants === 0) {
		return `Tem certeza que deseja remover o item “${item.name}”? Esta ação não pode ser desfeita.`;
	}
	const plural = descendants === 1 ? "sub-item" : "sub-itens";
	return `Esta ação removerá também ${descendants} ${plural} de “${item.name}” e não pode ser desfeita.`;
}

interface RowActions {
	canCreate: boolean;
	canUpdate: boolean;
	canDelete: boolean;
	onAddChild: (item: TemplateItem) => void;
	onEdit: (item: TemplateItem) => void;
	onDelete: (item: TemplateItem) => void;
	onToggleCollapsed: (id: string) => void;
}

function SortableItemRow({
	item,
	childCount,
	isCollapsed,
	actions,
}: {
	item: TemplateItem;
	childCount: number;
	isCollapsed: boolean;
	actions: RowActions;
}) {
	const {
		canCreate,
		canUpdate,
		canDelete,
		onAddChild,
		onEdit,
		onDelete,
		onToggleCollapsed,
	} = actions;
	const canAddChild = canCreate && item.kind !== "service";
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id, disabled: !canUpdate });

	return (
		<TableRow
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={clsx("template-items-table__row", isDragging && "is-dragging")}
		>
			<TableCell className="template-items-table__name-cell">
				<div
					className="template-items-table__name"
					style={{ paddingLeft: `${item.depth * 1.5}rem` }}
				>
					{canUpdate && (
						<button
							type="button"
							ref={setActivatorNodeRef}
							{...attributes}
							{...listeners}
							aria-label={`Reordenar ${item.name}`}
							className="template-items-table__grip"
						>
							<GripVertical />
						</button>
					)}
					{childCount > 0 ? (
						<Button
							variant="ghost"
							size="icon"
							className="template-items-table__toggle"
							onClick={() => onToggleCollapsed(item.id)}
							aria-label={
								isCollapsed ? `Expandir ${item.name}` : `Recolher ${item.name}`
							}
						>
							{isCollapsed ? <ChevronRight /> : <ChevronDown />}
						</Button>
					) : (
						<span className="template-items-table__spacer" aria-hidden />
					)}
					<div className="template-items-table__info">
						<div className="template-items-table__name-line">
							<p className="template-items-table__item-name">{item.name}</p>
							<Badge
								variant="outline"
								className="kind-badge"
								data-kind={item.kind}
							>
								{STAGE_KIND[item.kind].label}
							</Badge>
						</div>
						{item.description && (
							<p className="template-items-table__item-desc">
								{item.description}
							</p>
						)}
					</div>
				</div>
			</TableCell>
			<TableCell className="template-items-table__actions-cell">
				<div className="template-items-table__actions">
					{canAddChild && (
						<Button
							variant="ghost"
							size="icon"
							className="template-items-table__action"
							onClick={() => onAddChild(item)}
							aria-label={`Adicionar sub-item em ${item.name}`}
						>
							<Plus />
						</Button>
					)}
					{canUpdate && (
						<Button
							variant="ghost"
							size="icon"
							className="template-items-table__action"
							onClick={() => onEdit(item)}
							aria-label={`Editar ${item.name}`}
						>
							<Pencil />
						</Button>
					)}
					{canDelete && (
						<Button
							variant="ghost"
							size="icon"
							className="template-items-table__action-delete"
							onClick={() => onDelete(item)}
							aria-label={`Remover ${item.name}`}
						>
							<Trash2 />
						</Button>
					)}
				</div>
				{(canAddChild || canUpdate || canDelete) && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="template-items-table__action-menu"
								aria-label={`Abrir menu de ${item.name}`}
							>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{canAddChild && (
								<DropdownMenuItem onClick={() => onAddChild(item)}>
									<Plus />
									Adicionar sub-item
								</DropdownMenuItem>
							)}
							{canUpdate && (
								<DropdownMenuItem onClick={() => onEdit(item)}>
									<Pencil />
									Editar
								</DropdownMenuItem>
							)}
							{(canAddChild || canUpdate) && canDelete && (
								<DropdownMenuSeparator />
							)}
							{canDelete && (
								<DropdownMenuItem
									variant="destructive"
									onClick={() => onDelete(item)}
								>
									<Trash2 />
									Remover
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</TableCell>
		</TableRow>
	);
}

type ItemDialogState =
	| { mode: "create"; defaultParentId?: string }
	| { mode: "edit"; item: TemplateItem }
	| null;

export function TemplateItemsTable({
	templateId,
	items,
}: {
	templateId: string;
	items: TemplateItem[];
}) {
	const { has } = usePermissions();
	const canCreate = has("stage_templates:create");
	const canUpdate = has("stage_templates:update");
	const canDelete = has("stage_templates:delete");

	const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
	const [itemDialog, setItemDialog] = useState<ItemDialogState>(null);
	const [itemToDelete, setItemToDelete] = useState<TemplateItem | null>(null);
	const deleteItem = useDeleteTemplateItem(templateId);
	const reorderItem = useReorderTemplateItem(templateId);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const counts = useMemo(() => childrenCount(items), [items]);
	const rows = useMemo(
		() => visibleItems(items, collapsed),
		[items, collapsed],
	);
	const descendantsToDelete = itemToDelete
		? descendantIds(items, itemToDelete.id).size
		: 0;

	function toggleCollapsed(id: string) {
		setCollapsed((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}

	function handleConfirmDelete() {
		if (!itemToDelete) return;
		deleteItem.mutate(itemToDelete.id, {
			onSuccess: () => {
				toast.success("Item removido com sucesso!");
				setItemToDelete(null);
			},
			onError: () => {
				toast.error("Houve um erro ao remover o item.");
				setItemToDelete(null);
			},
		});
	}

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const move = moveSiblingBlock(items, String(active.id), String(over.id));
		if (!move) {
			toast.info(
				"Arraste apenas entre itens do mesmo nível. Para mudar o nível, edite o item e troque o item pai.",
			);
			return;
		}
		reorderItem.mutate(move, {
			onError: () => toast.error("Houve um erro ao reordenar os itens."),
		});
	}

	const actions: RowActions = {
		canCreate,
		canUpdate,
		canDelete,
		onAddChild: (item) =>
			setItemDialog({ mode: "create", defaultParentId: item.id }),
		onEdit: (item) => setItemDialog({ mode: "edit", item }),
		onDelete: setItemToDelete,
		onToggleCollapsed: toggleCollapsed,
	};

	return (
		<>
			<div className="template-items-table__header">
				<div>
					<h2 className="template-items-table__title">Itens</h2>
					<p className="template-items-table__subtitle">
						Organize as etapas e sub-etapas que o template vai gerar.
					</p>
				</div>
				{canCreate && (
					<Button onClick={() => setItemDialog({ mode: "create" })}>
						<Plus />
						Novo item
					</Button>
				)}
			</div>

			<Card className="template-items-table__card">
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<Table>
						<TableHeader className="template-items-table__table-header">
							<TableRow>
								<TableHead className="template-items-table__head-name">
									Nome
								</TableHead>
								<TableHead className="template-items-table__actions-cell">
									Ações
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={2}
										className="template-items-table__empty"
									>
										Nenhum item neste template ainda.
									</TableCell>
								</TableRow>
							) : (
								<SortableContext
									items={rows.map((item) => item.id)}
									strategy={verticalListSortingStrategy}
								>
									{rows.map((item) => (
										<SortableItemRow
											key={item.id}
											item={item}
											childCount={counts.get(item.id) ?? 0}
											isCollapsed={collapsed.has(item.id)}
											actions={actions}
										/>
									))}
								</SortableContext>
							)}
						</TableBody>
					</Table>
				</DndContext>
			</Card>

			{itemDialog && (
				<TemplateItemDialog
					open
					onOpenChange={(open) => {
						if (!open) setItemDialog(null);
					}}
					templateId={templateId}
					mode={itemDialog.mode}
					items={items}
					item={itemDialog.mode === "edit" ? itemDialog.item : undefined}
					defaultParentId={
						itemDialog.mode === "create"
							? itemDialog.defaultParentId
							: undefined
					}
				/>
			)}

			<ConfirmDialog
				open={!!itemToDelete}
				onOpenChange={(open) => {
					if (!open) setItemToDelete(null);
				}}
				title="Remover item"
				description={
					itemToDelete
						? deleteDescription(itemToDelete, descendantsToDelete)
						: ""
				}
				confirmLabel="Remover"
				destructive
				isPending={deleteItem.isPending}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
}

export function TemplateItemsTableSkeleton() {
	return (
		<>
			<div className="template-items-table__header">
				<div className="template-items-table__skeleton-heading">
					<Skeleton className="template-items-table__skeleton-title" />
					<Skeleton className="template-items-table__skeleton-subtitle" />
				</div>
			</div>
			<Card className="template-items-table__card">
				<div className="template-items-table__skeleton">
					<Skeleton className="template-items-table__skeleton-row" />
					<Skeleton className="template-items-table__skeleton-row" />
					<Skeleton className="template-items-table__skeleton-row" />
					<Skeleton className="template-items-table__skeleton-row" />
				</div>
			</Card>
		</>
	);
}
